import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import clsx from "clsx";
import { Grid, IconButton, Card, Select, MenuItem } from "@material-ui/core";
import { format, lastDayOfMonth, startOfDay } from "date-fns";
import { useStyles } from "./simple-calendar.styles";

interface DayConfig {
  date: Date;
  customCssClass?: string;
  disabled?: boolean;
}

interface CalendarProps {
  initialMonth?: Date;
  readonly?: boolean;
  selectedDates: DayConfig[];
  allowFutureDates?: boolean;
  allowPastDates?: boolean;

  onDayClicked?: (e: Date) => void;
  onMonthSelectedChanged?: (e: Date) => void;
}

interface DayProps {
  isSelected: boolean;
  number?: number;
  date?: Date;
  day?: string;
  isToday?: boolean;
  isPast?: boolean;
  isDisabled: boolean;
  hidden: boolean;
  customCssClass?: string;
}

const today = startOfDay(new Date());
const todayMonthIndex = format(today, "M");

const months = {
  jan: { name: "January", index: 1 },
  feb: { name: "February", index: 2 },
  mar: { name: "March", index: 3 },
  apr: { name: "April", index: 4 },
  may: { name: "May", index: 5 },
  jun: { name: "June", index: 6 },
  jul: { name: "July", index: 7 },
  aug: { name: "August", index: 8 },
  sep: { name: "September", index: 9 },
  oct: { name: "October", index: 10 },
  nov: { name: "November", index: 11 },
  dec: { name: "December", index: 12 },
};

const weekDays = {
  Mo: { shortName: "M", index: 1 },
  Tu: { shortName: "T", index: 2 },
  We: { shortName: "W", index: 3 },
  Th: { shortName: "T", index: 4 },
  Fr: { shortName: "F", index: 5 },
  Sa: { shortName: "S", index: 6 },
  Su: { shortName: "S", index: 7 },
};

const SimpleCalendar = ({
  selectedDates,
  initialMonth = new Date(),
  readonly = false,
  allowFutureDates = false,
  allowPastDates = false,
  onDayClicked = undefined,
  onMonthSelectedChanged = undefined,
}: CalendarProps) => {
  const classes = useStyles();
  const [currentDate, setCurrentDate] = useState(initialMonth);
  const [calendarDays, setCalendarDays] = useState<DayProps[]>();

  const [currentMonthIndex, currentMonthName] = format(
    currentDate,
    "M-MMMM yyyy"
  ).split("-");
  const firstDateMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const lastDateMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );
  const fDayWeek = format(firstDateMonth, "iiiiii");
  const lDayWeek = format(lastDateMonth, "iiiiii");

  const fillEmptyArray = (n: number, arrayMonth: DayProps[]): DayProps[] => {
    return Array(n)
      .fill({})
      .map((v, i) => ({
        isSelected: false,
        isWeekSelected: false,
        isDisabled: true,
        number: arrayMonth.length + i + 1,
        hidden: true,
      }));
  };

  useEffect(() => {
    const arrayMonth: DayProps[] = Array(
      Number(format(lastDayOfMonth(currentDate), "d"))
    )
      .fill({})
      .map((v, i) => {
        const daynum = i + 1;
        const itemDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          daynum
        );
        const day = format(itemDate, "EEEEEE");

        const config = selectedDates?.find(
          (c) => format(c.date, "yyyy-MM-d") === format(itemDate, "yyyy-MM-d")
        );

        const isToday =
          format(itemDate, "yyyy-MM-d") === format(today, "yyyy-MM-d");
        const customCssClass = config?.customCssClass ?? "";
        const isSelected = config != null && !config.disabled;
        const isPast = itemDate < new Date(format(today, "yyyy-MM-d"));
        const isFuture = itemDate > today;
        const isDisabled =
          config?.disabled ||
          (isPast && !allowPastDates && !readonly) ||
          (isFuture && !allowFutureDates && !readonly) ||
          ["Sa", "Su"].includes(day);

        return {
          ...v,
          number: daynum,
          date: itemDate,
          day,
          isToday,
          isPast,
          isDisabled,
          isSelected,
          customCssClass,
        };
      });

    if (weekDays[fDayWeek].index > 1)
      arrayMonth.unshift(
        ...fillEmptyArray(weekDays[fDayWeek].index - 1, arrayMonth)
      );

    if (weekDays[lDayWeek].index > 1)
      arrayMonth.push(
        ...fillEmptyArray(7 - weekDays[lDayWeek].index, arrayMonth)
      );

    setCalendarDays(arrayMonth);
  }, [currentDate, selectedDates]);

  const notifySelectedMonthChangedTo = (newDate: Date) => {
    if (typeof onMonthSelectedChanged === "function") {
      onMonthSelectedChanged(newDate);
    }
  };

  const handlePrevMonthClick = () => {
    const nextMonthIndex = Number(currentMonthIndex) - 1;
    if (
      nextMonthIndex < 1 ||
      (nextMonthIndex < Number(todayMonthIndex) && !allowPastDates)
    ) {
      return;
    }

    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1
    );

    setCurrentDate(newDate);
    notifySelectedMonthChangedTo(newDate);
  };

  const handleNextMonthClick = () => {
    const nextMonthIndex = Number(currentMonthIndex) + 1;
    if (
      nextMonthIndex > 12 ||
      (nextMonthIndex > Number(todayMonthIndex) && !allowFutureDates)
    ) {
      return;
    }

    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1
    );

    setCurrentDate(newDate);
    notifySelectedMonthChangedTo(newDate);
  };

  const handleSelectDayClick = (e: Date) => {
    if (!readonly && typeof onDayClicked === "function") {
      onDayClicked(e);
    }
  };

  const handleSelectChange = (e) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      Number(e.target.value) - 1,
      1
    );

    setCurrentDate(newDate);
    notifySelectedMonthChangedTo(newDate);
  };

  const renderMonths = () => {
    const currrentMonthIndex = Number(todayMonthIndex);

    return Object.values(months).map((month) => {
      if (
        month.index === currrentMonthIndex ||
        (month.index < currrentMonthIndex && allowPastDates) ||
        (month.index > currrentMonthIndex && allowFutureDates)
      ) {
        return (
          <MenuItem key={`month-${month.index}`} value={month.index}>
            {month.name}
          </MenuItem>
        );
      }
      return null;
    });
  };

  return (
    <div className={classes.parent}>
      <Card className={classes.container}>
        <Grid container>
          <Grid item container xs={12} className={classes.header}>
            <Select
              className={classes.title}
              value={currentMonthName}
              onChange={handleSelectChange}
            >
              <MenuItem
                className={classes.hiddenMenuItem}
                value={currentMonthName}
              >
                {currentMonthName}
              </MenuItem>
              {renderMonths()}
            </Select>
            <Grid item>
              <IconButton onClick={handlePrevMonthClick}>
                <ChevronLeft />
              </IconButton>
              <IconButton onClick={handleNextMonthClick}>
                <ChevronRight />
              </IconButton>
            </Grid>
          </Grid>
          <Grid item container className={classes.calendar}>
            {Object.values(weekDays).map((week) => (
              <Grid item key={week.index} className={classes.weekDay}>
                {week.shortName}
              </Grid>
            ))}
          </Grid>
          <Grid
            item
            container
            className={clsx(classes.calendar, { "is-readonly": readonly })}
          >
            {calendarDays?.map((day) => (
              <Grid
                item
                key={`calendar-day-${day.number}`}
                className={classes.calendarItem}
              >
                <IconButton
                  onClick={() => handleSelectDayClick(day.date)}
                  className={clsx(classes.day, day.customCssClass, {
                    [classes.selected]: day.isSelected,
                    [classes.current]: day.isToday,
                  })}
                  disabled={day.isDisabled}
                >
                  {!day.hidden && day.number}
                </IconButton>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Card>
    </div>
  );
};

SimpleCalendar.defaultProps = {
  readonly: false,
  allowFutureDates: false,
  allowPastDates: false,
  initialMonth: new Date(),
  onDayClicked: undefined,
  onMonthSelectedChanged: undefined,
};

export default SimpleCalendar;
export type { DayConfig };
